/**
 * QueueDisplayMenu: the Order Queue's "Display" menu. Each person picks which
 * badges their queue rows show; the choice is saved to their account.
 *
 * Toggles apply instantly and keep the menu open. `onOpenChange` drives the
 * single save: the hook snapshots on open and sends at most one write on close
 * (useOrderQueueDisplay).
 */
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { OrderQueueBadgeKey, OrderQueueBadgeVisibility } from '../../display-prefs';

export interface QueueDisplayMenuProps {
  visibility: OrderQueueBadgeVisibility;
  onChange: (key: OrderQueueBadgeKey, on: boolean) => void;
  onReset: () => void;
  onOpenChange: (open: boolean) => void;
}

const ROW_ITEMS: ReadonlyArray<[OrderQueueBadgeKey, string]> = [
  ['fulfilmentType', 'orders.display.fulfilmentType'],
  ['holdCountdown', 'orders.display.holdCountdown'],
  ['resolved', 'orders.resolved'],
  ['archivedSku', 'orders.archivedSku.label'],
];

const PORTLESS_ITEMS: ReadonlyArray<[OrderQueueBadgeKey, string]> = [
  ['portlessLabelNotReady', 'orders.portlessBlocked.label'],
  ['portlessLabelReady', 'portlessFulfilment.labelReady'],
  ['hybrid', 'portlessFulfilment.hybrid'],
];

export function QueueDisplayMenu({ visibility, onChange, onReset, onOpenChange }: QueueDisplayMenuProps) {
  const { t } = useTranslation('production');

  const item = ([key, labelKey]: [OrderQueueBadgeKey, string]) => (
    <DropdownMenuCheckboxItem
      key={key}
      checked={visibility[key]}
      // Keep the menu open while toggling.
      onSelect={(event) => event.preventDefault()}
      onCheckedChange={() => onChange(key, !visibility[key])}
      className="whitespace-nowrap"
    >
      {t(labelKey)}
    </DropdownMenuCheckboxItem>
  );

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground">
          <SlidersHorizontal aria-hidden="true" />
          {t('orders.display.button')}
        </Button>
      </DropdownMenuTrigger>
      {/* `!`: the primitive's data-[state=open]:animate-in outranks a plain
          motion-reduce:animate-none (attribute selector, higher specificity). */}
      <DropdownMenuContent align="end" className="w-max min-w-[14rem] motion-reduce:!animate-none">
        <DropdownMenuLabel>{t('orders.display.heading')}</DropdownMenuLabel>
        {ROW_ITEMS.map(item)}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('orders.display.portlessHeading')}</DropdownMenuLabel>
        {PORTLESS_ITEMS.map(item)}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onReset()}>{t('orders.display.reset')}</DropdownMenuItem>
        <p className="px-2 pb-1 pt-1.5 text-xs text-muted-foreground">{t('orders.display.savedNote')}</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
